import { saveFile } from '../lib/download'
import { toFrontmatter } from '../lib/frontmatter'
import { htmlToMarkdown } from '../lib/markdown'
import { SITE } from '../lib/seo'
import { docPath, type DocDetail } from '../lib/types'
import { Button } from './ui'

/**
 * Mirror the canonical URL, flattened: `core-backend-api-prescribing-erx-erx-frontend-flow.md`.
 * The slug alone would collide the moment two services each have an “Overview”, and
 * a file in someone's `docs/` folder should say where it came from.
 */
export const docFilename = (doc: DocDetail) => `${docPath(doc).slice(1).replace(/\//g, '-')}.md`

/** The doc as the file it was authored as: frontmatter, then the body. */
export function docToMarkdown(doc: DocDetail, html: string): string {
  return toFrontmatter(
    {
      service: doc.service,
      topic: doc.topic,
      title: doc.title,
      description: doc.description,
      visibility: doc.visibility,
      tags: doc.tags,
      markdown: htmlToMarkdown(html),
    },
    // Provenance the authoring shape has no field for. Whoever — or whatever —
    // reads this file later is outside the wiki and cannot see the URL bar.
    { source: `${SITE}${docPath(doc)}`, updated: doc.updated_at },
  )
}

/**
 * Download this one page as Markdown, so it can be dropped into a repo and handed
 * to an assistant in an IDE.
 *
 * The body is converted from the HTML already on screen — no second fetch, and
 * nothing the reader cannot already see, so this needs no permission check of its
 * own. `visibility: internal` rides along in the frontmatter because the file
 * outlives the page it came from, and by then nothing else says so.
 */
export function DownloadDoc({ doc, html }: { doc: DocDetail; html: string | null }) {
  return (
    <Button
      size="sm"
      variant="secondary"
      // The body arrives a moment after the metadata; keep the slot, not the promise.
      disabled={html === null}
      title={`Download ${docFilename(doc)}`}
      onClick={() => html && saveFile(docFilename(doc), docToMarkdown(doc, html), 'text/markdown')}
    >
      <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v10m0 0l-4-4m4 4l4-4M5 18h14" />
      </svg>
      Download .md
    </Button>
  )
}
